/**
 * Thumbs / report control shown under an in-product verification.
 *
 * Explicit user feedback is the ground-truth signal for the verification-
 * improvement loop. Best-effort by design: a failed submit never disrupts the
 * user — we optimistically show the acknowledgement and swallow the rejection.
 */

import { useState } from 'react'
import { factCheckApi } from '../services/api'

type Signal = 'helpful' | 'not_helpful' | 'report'

interface VerdictFeedbackProps {
  /** fact_checks.id of the verification being rated. */
  factCheckId: string
  /** chat_messages.id of the turn shown (lets mining point at the exact message). */
  messageId?: string
}

export function VerdictFeedback({ factCheckId, messageId }: VerdictFeedbackProps) {
  const [submitted, setSubmitted] = useState<Signal | null>(null)
  const [reporting, setReporting] = useState(false)
  const [reason, setReason] = useState('')

  const send = (signal: Signal, extra?: string) => {
    // Optimistic — acknowledge immediately, submit in the background.
    setSubmitted(signal)
    factCheckApi.submitFeedback(factCheckId, signal, { reason: extra, messageId }).catch(() => {
      /* best-effort: never disrupt the user on a feedback failure */
    })
  }

  if (submitted && !(reporting && submitted !== 'report')) {
    return (
      <div className="mt-3 text-xs text-ink-400">
        {submitted === 'report' ? 'Thanks — this verdict has been flagged for review.' : 'Thanks for the feedback.'}
      </div>
    )
  }

  if (reporting) {
    return (
      <div className="mt-3 flex flex-col gap-2 max-w-md">
        <label className="text-xs text-ink-400" htmlFor={`report-${factCheckId}`}>
          What's wrong with this verdict? (optional)
        </label>
        <textarea
          id={`report-${factCheckId}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          maxLength={2000}
          className="w-full rounded-lg border border-paper-200 bg-paper-50 px-3 py-2 text-sm text-ink-700 focus:border-accent-500 focus:outline-none"
          placeholder="e.g. the sources don't support this, or a fact is out of date"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => send('report', reason.trim() || undefined)}
            className="rounded-full bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 transition-colors"
          >
            Submit report
          </button>
          <button
            type="button"
            onClick={() => setReporting(false)}
            className="rounded-full border border-paper-200 px-3 py-1.5 text-xs text-ink-600 hover:border-ink-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-3 flex items-center gap-3 text-ink-400">
      <span className="text-xs">Was this helpful?</span>
      <button
        type="button"
        aria-label="Helpful"
        onClick={() => send('helpful')}
        className="inline-flex items-center gap-1 rounded-full border border-paper-200 px-2.5 py-1 text-xs hover:border-verdict-true hover:text-verdict-true transition-colors"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M7 10v12" />
          <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
        </svg>
        Yes
      </button>
      <button
        type="button"
        aria-label="Not helpful"
        onClick={() => send('not_helpful')}
        className="inline-flex items-center gap-1 rounded-full border border-paper-200 px-2.5 py-1 text-xs hover:border-verdict-mixed hover:text-verdict-mixed transition-colors"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M17 14V2" />
          <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
        </svg>
        No
      </button>
      <button
        type="button"
        onClick={() => setReporting(true)}
        className="text-xs underline decoration-dotted hover:text-verdict-false transition-colors"
      >
        Report
      </button>
    </div>
  )
}
